import { Component, inject, ViewChild, AfterViewInit, ChangeDetectionStrategy } from '@angular/core';

import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActualiteService } from '../../../_services/actualite/actualite.service';
import { FirebaseStorageService } from '../../../_services/firebase-storage/firebase-storage.service';
import { Actualite } from '../../../_interfaces/actualite';
import { QuillModule, QuillEditorComponent } from 'ngx-quill';
import { Router } from '@angular/router';
import { TagType, TAG_CONFIG } from '../../../_enums/tag.enum';
import { FormsModule } from '@angular/forms';
import { AdminPageHeaderComponent } from '../../../_components/admin-page-header/admin-page-header.component';
import { ActualiteContentComponent } from '../../../_components/actualite-content/actualite-content.component';

@Component({
    selector: 'app-actualite-edition',
    imports: [ReactiveFormsModule, FormsModule, QuillModule, AdminPageHeaderComponent, ActualiteContentComponent],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './actualite-edition.component.html'
})
export class ActualiteEditionComponent implements AfterViewInit {
  private actualiteService = inject(ActualiteService);
  private firebaseStorageService = inject(FirebaseStorageService);
  private fb = inject(FormBuilder);
  private router = inject(Router);

  @ViewChild('editor') editor!: QuillEditorComponent;
  quillInstance: any;

  actualiteForm: FormGroup;
  actualite?: Actualite;
  actualites: Actualite[] = [];
  imageFile?: File;
  isModalOpen = false;
  isDeleteModalOpen = false;
  actualiteToDelete?: Actualite;
  isPreviewOpen = false;
  previewActualite?: Actualite;

  private readonly MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  private readonly MAX_WIDTH = 1200;
  private readonly MAX_HEIGHT = 800;
  private readonly QUALITY = 0.7;

  modules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ 'header': 1 }, { 'header': 2 }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'script': 'sub'}, { 'script': 'super' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'direction': 'rtl' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ]
  };

  // Reprend les styles du rendu public (actualite-content.component.scss) pour que
  // l'aperçu pendant l'écriture ressemble au résultat final.
  styles = `
    .ql-editor {
      font-size: 1.125rem;
      line-height: 1.75;
    }
    .ql-editor img {
      display: block;
      margin: 2rem auto;
      max-width: 100%;
      height: auto;
      border-radius: 0.5rem;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    }
    .ql-editor .ql-align-center { text-align: center; }
    .ql-editor .ql-align-right { text-align: right; }
    .ql-editor .ql-align-left { text-align: left; }
    .ql-editor .ql-align-justify { text-align: justify; }
    .ql-editor h1 {
      font-size: 2em;
      margin: 1em 0 0.5em;
      font-family: 'Alice', serif;
      color: #22232B;
    }
    .ql-editor h2 {
      font-size: 1.5em;
      margin: 1em 0 0.5em;
      font-family: 'Alice', serif;
      color: #22232B;
    }
    .ql-editor strong { font-weight: 600; color: #22232B; }
    .ql-editor a { color: #3B4193; text-decoration: underline; font-weight: 500; }
    .ql-editor blockquote {
      border-left: 4px solid #3B4193;
      margin: 1.5em 0;
      padding-left: 1em;
      font-style: italic;
      color: #4a5568;
    }
    .ql-editor pre {
      background-color: #f7fafc;
      border-radius: 0.375rem;
      padding: 1em;
      margin: 1em 0;
      font-size: 0.875em;
    }
    .ql-editor code {
      background-color: #f7fafc;
      padding: 0.2em 0.4em;
      border-radius: 0.25rem;
      font-size: 0.875em;
    }
  `;

  // Variable pour stocker temporairement le fichier image
  selectedImageFile: File | null = null;

  tagTypes = Object.values(TagType);
  tagConfig = TAG_CONFIG;
  search = '';

  get filteredActualites(): Actualite[] {
    const q = this.search.trim().toLowerCase();
    if (!q) return this.actualites;
    return this.actualites.filter(a =>
      (a.titre || '').toLowerCase().includes(q) ||
      (a.auteur || '').toLowerCase().includes(q)
    );
  }

  formatDate(value: string | undefined): string {
    if (!value) return '';
    return new Date(value).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  constructor() {
    this.actualiteForm = this.fb.group({
      titre: ['', Validators.required],
      contenu: ['', Validators.required],
      auteur: ['', Validators.required],
      imagePreview: [''],
      image: [''],
      date: ['', Validators.required],
      tags: [[]],
      statut: ['publie']
    });
    
    this.loadActualites();
  }

  ngAfterViewInit() {
    // Ajoute les styles personnalisés
    const styleElement = document.createElement('style');
    styleElement.textContent = this.styles;
    document.head.appendChild(styleElement);

    this.editor.onEditorCreated.subscribe((quill) => {
      this.quillInstance = quill;
      (quill.getModule('toolbar') as { addHandler: (name: string, handler: () => void) => void })
        .addHandler('image', () => this.imageHandler());
      quill.clipboard.addMatcher(Node.ELEMENT_NODE, (_node: Node, delta: any) => {
        delta.ops?.forEach((op: any) => {
          const src = op.insert?.image;
          if (typeof src === 'string' && src.startsWith('data:image/')) {
            this.uploadPastedImage(src);
          }
        });
        return delta;
      });
    });
  }

  ngOnInit() {
    // Si on a une actualité à modifier, on initialise le formulaire avec ses données
    if (this.actualite) {
      this.actualiteForm.patchValue({
        titre: this.actualite.titre,
        contenu: this.actualite.contenu,
        auteur: this.actualite.auteur || '',
        image: this.actualite.image || '',
        date: new Date(this.actualite.date).toISOString().split('T')[0],
        tags: this.actualite.tags || []
      });
    }
  }

  private async resizeImage(file: File): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          if (width > this.MAX_WIDTH) {
            height = Math.round(height * (this.MAX_WIDTH / width));
            width = this.MAX_WIDTH;
          }
          if (height > this.MAX_HEIGHT) {
            width = Math.round(width * (this.MAX_HEIGHT / height));
            height = this.MAX_HEIGHT;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Échec de la conversion en blob'));
              }
            },
            'image/jpeg',
            this.QUALITY
          );
        };
      };
      reader.onerror = (error) => reject(error);
    });
  }

  async processImageUpload(file: File): Promise<string> {
    if (file.size > this.MAX_FILE_SIZE) {
      throw new Error(`L'image est trop grande. Taille maximum: ${this.MAX_FILE_SIZE / (1024 * 1024)}MB`);
    }

    try {
      const resizedImage = await this.resizeImage(file);
      const path = `actualites/content/${Date.now()}_${file.name}`;
      return await this.firebaseStorageService.uploadFIle(
        new File([resizedImage], file.name, { type: 'image/jpeg' }), 
        path
      );
    } catch (error) {
      console.error('Erreur lors du traitement de l\'image:', error);
      throw error;
    }
  }

  imageHandler() {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      if (input.files && input.files[0] && this.quillInstance) {
        try {
          const url = await this.processImageUpload(input.files[0]);
          const range = this.quillInstance.getSelection(true);
          this.quillInstance.insertEmbed(range.index, 'image', url);
          this.quillInstance.setSelection(range.index + 1);
        } catch (error) {
          alert(error instanceof Error ? error.message : 'Erreur lors de l\'upload de l\'image');
        }
      }
    };
  }

  private async uploadPastedImage(base64: string) {
    try {
      const blob = await (await fetch(base64)).blob();
      const ext = blob.type.split('/')[1] || 'png';
      const file = new File([blob], `paste-${Date.now()}.${ext}`, { type: blob.type });
      const url = await this.processImageUpload(file);

      const ops = this.quillInstance.getContents().ops;
      let index = 0;
      for (const op of ops) {
        if (op.insert?.image === base64) {
          this.quillInstance.deleteText(index, 1, 'silent');
          this.quillInstance.insertEmbed(index, 'image', url, 'user');
          break;
        }
        index += typeof op.insert === 'string' ? op.insert.length : 1;
      }
    } catch (error) {
      console.error('Erreur lors du remplacement de l\'image collée:', error);
      alert('Une image collée n\'a pas pu être téléversée et a été laissée telle quelle. Vérifiez sa taille (max 5MB).');
    }
  }

  private containsBase64Image(html: string): boolean {
    return /src=["']data:image\//.test(html);
  }

  loadActualites() {
    this.actualiteService.readAllActualites().subscribe(
      actualites => this.actualites = actualites
    );
  }

  async onSubmit() {
    if (this.actualiteForm.valid) {
      if (this.containsBase64Image(this.actualiteForm.get('contenu')?.value || '')) {
        alert('Une image du contenu n\'a pas encore été téléversée. Attendez la fin de l\'upload avant d\'enregistrer, ou retirez l\'image concernée.');
        return;
      }
      try {
        let imageUrl = this.actualiteForm.get('image')?.value;
        
        if (this.selectedImageFile) {
          const resizedImage = await this.resizeImage(this.selectedImageFile);
          const path = `actualites/covers/${Date.now()}_${this.selectedImageFile.name}`;
          imageUrl = await this.firebaseStorageService.uploadFIle(
            new File([resizedImage], this.selectedImageFile.name, { type: 'image/jpeg' }), 
            path
          );
        } else if (this.actualite?.image) {
          imageUrl = this.actualite.image;
        }

        // Récupération et formatage de la date
        const formDate = this.actualiteForm.get('date')?.value;
        console.log(formDate);
        const selectedDate = new Date(formDate);
        selectedDate.setHours(12, 0, 0, 0); // Midi pour éviter les problèmes de fuseau horaire

        const actualiteData: Actualite = {
          titre: this.actualiteForm.get('titre')?.value || '',
          contenu: this.actualiteForm.get('contenu')?.value || '',
          auteur: this.actualiteForm.get('auteur')?.value || '',
          date: selectedDate.toISOString(),
          image: imageUrl,
          tags: this.actualiteForm.get('tags')?.value || [],
          statut: this.actualiteForm.get('statut')?.value || 'publie'
        };

        if (this.actualite?.key) {
          await this.actualiteService.updateActualite(this.actualite.key, actualiteData);
        } else {
          await this.actualiteService.createActualite(actualiteData);
        }

        this.selectedImageFile = null;
        this.actualiteForm.reset();
        this.router.navigate(['/admin/actualites']);
      } catch (error) {
        console.error('Erreur lors de la soumission:', error);
        alert('Erreur lors de la sauvegarde de l\'actualité');
      }
    }
  }

  openModal() {
    this.actualite = undefined;
    this.actualiteForm.reset();
    this.actualiteForm.patchValue({ statut: 'publie' });
    this.isModalOpen = true;
  }

  closeModal() {
    this.isModalOpen = false;
    this.actualite = undefined;
    this.actualiteForm.reset();
  }

  onEdit(actualite: Actualite) {
    this.actualite = actualite;
    this.actualiteForm.patchValue({
      titre: actualite.titre,
      contenu: actualite.contenu,
      auteur: actualite.auteur || '',
      image: actualite.image || '',
      date: new Date(actualite.date).toISOString().split('T')[0],
      tags: actualite.tags || [],
      statut: actualite.statut || 'publie'
    });
    this.isModalOpen = true;
  }

  openPreview() {
    const formDate = this.actualiteForm.get('date')?.value;
    this.previewActualite = {
      titre: this.actualiteForm.get('titre')?.value || '(Sans titre)',
      contenu: this.actualiteForm.get('contenu')?.value || '',
      auteur: this.actualiteForm.get('auteur')?.value || '',
      date: formDate ? new Date(formDate).toISOString() : new Date().toISOString(),
      image: this.actualiteForm.get('imagePreview')?.value || this.actualiteForm.get('image')?.value || '',
      tags: this.actualiteForm.get('tags')?.value || []
    };
    this.isPreviewOpen = true;
  }

  closePreview() {
    this.isPreviewOpen = false;
    this.previewActualite = undefined;
  }

  onDelete(actualite: Actualite) {
    this.actualiteToDelete = actualite;
    this.isDeleteModalOpen = true;
  }

  closeDeleteModal() {
    this.isDeleteModalOpen = false;
    this.actualiteToDelete = undefined;
  }

  async confirmDelete() {
    if (this.actualiteToDelete?.key) {
      try {
        await this.actualiteService.deleteActualite(this.actualiteToDelete.key);
        this.loadActualites();
        this.closeDeleteModal();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
      }
    }
  }

  toggleDropdown(key: string, isMobile: boolean) {
    const dropdownId = `dropdown-${isMobile ? '' : 'desktop-'}${key}`;
    const buttonId = `dropdownButton-${isMobile ? '' : 'desktop-'}${key}`;
    const dropdown = document.getElementById(dropdownId);
    const button = document.getElementById(buttonId);

    // Ferme tous les autres dropdowns
    document.querySelectorAll('[id^="dropdown-"]').forEach(el => {
      if (el.id !== dropdownId) {
        el.classList.add('hidden');
      }
    });

    if (dropdown && button) {
      const buttonRect = button.getBoundingClientRect();
      const isHidden = dropdown.classList.contains('hidden');

      // Toggle le dropdown
      dropdown.classList.toggle('hidden');

      if (!isHidden) return; // Si on cache le dropdown, pas besoin de calculer la position

      // Position the dropdown relative to the button
      if (window.innerWidth - buttonRect.right < dropdown.offsetWidth) {
        // If there's not enough space on the right, align to the right edge of the button
        dropdown.style.right = '0';
        dropdown.style.left = 'auto';
      } else {
        // Otherwise, align to the left edge of the button
        dropdown.style.left = '0';
        dropdown.style.right = 'auto';
      }
    }
  }

  onReset() {
    this.actualite = undefined;
    this.imageFile = undefined;
    this.actualiteForm.reset();
  }

  onImageSelected(event: any) {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > this.MAX_FILE_SIZE) {
      alert(`L'image est trop grande. Taille maximum: ${this.MAX_FILE_SIZE / (1024 * 1024)}MB`);
      return;
    }

    // Stocke le fichier pour l'upload ultérieur
    this.selectedImageFile = file;

    // Affiche uniquement la prévisualisation
    const reader = new FileReader();
    reader.onload = (e) => {
      this.actualiteForm.patchValue({
        imagePreview: e.target?.result
      });
    };
    reader.readAsDataURL(file);
  }

  toggleTag(tag: TagType) {
    const currentTags = this.actualiteForm.get('tags')?.value || [];
    const index = currentTags.indexOf(tag);
    
    if (index === -1) {
      this.actualiteForm.patchValue({
        tags: [...currentTags, tag]
      });
    } else {
      currentTags.splice(index, 1);
      this.actualiteForm.patchValue({
        tags: currentTags
      });
    }
  }

  isTagSelected(tag: TagType): boolean {
    const currentTags = this.actualiteForm.get('tags')?.value || [];
    return currentTags.includes(tag);
  }
}
